/**
 * useSeatDrafts - Local (client-side) seat draft management for the Map Editor
 * Elvis Aloha Collection
 *
 * Newly drawn / placed seat boxes are kept LOCAL and unsaved until the owner
 * explicitly saves them (Save individually or Save All). This allows move,
 * resize, rotate, duplicate, delete, undo and cancel WITHOUT creating any
 * backend record. Persistence goes through POST /seat-map/seat-geometries,
 * which enforces bounds / duplicate / overlap / aisle validation.
 */

import { useState, useCallback, useRef } from 'react';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

let draftCounter = 0;
const newDraftId = () => `draft-${Date.now()}-${draftCounter++}`;

export const useSeatDrafts = (chartId, token, { onSaved } = {}) => {
  const [drafts, setDrafts] = useState([]);
  const [saving, setSaving] = useState(false);

  // Convenience memory: last-used section/row + next seat suggestion
  const memoryRef = useRef({ section: 'BB', row: '1', seat: 14 });

  const rememberMeta = useCallback((section, row, seat) => {
    memoryRef.current = {
      section: section ?? memoryRef.current.section,
      row: row ?? memoryRef.current.row,
      seat: seat ?? memoryRef.current.seat,
    };
  }, []);

  const suggestNextSeat = useCallback(() => {
    const cur = parseInt(memoryRef.current.seat, 10);
    return Number.isFinite(cur) ? String(cur) : '';
  }, []);

  const addDraft = useCallback((partial) => {
    const mem = memoryRef.current;
    const draft = {
      id: newDraftId(),
      chart_id: chartId,
      native_x: partial.native_x,
      native_y: partial.native_y,
      width: partial.width || 17,
      height: partial.height || 14,
      rotation: partial.rotation || 0,
      section: partial.section != null ? partial.section : (mem.section || ''),
      row: partial.row != null ? partial.row : (mem.row || ''),
      seat: partial.seat != null ? partial.seat : '',
      template_id: partial.template_id || null,
      size_locked: !!partial.size_locked,
      error: null,
      _local: true,
    };
    setDrafts(prev => [...prev, draft]);
    return draft;
  }, [chartId]);

  const updateDraft = useCallback((id, changes) => {
    setDrafts(prev => prev.map(d => (d.id === id ? { ...d, ...changes } : d)));
  }, []);

  const removeDraft = useCallback((id) => {
    setDrafts(prev => prev.filter(d => d.id !== id));
  }, []);

  const clearDrafts = useCallback(() => setDrafts([]), []);

  const duplicateDraft = useCallback((id, offset = { x: 0, y: 0 }) => {
    const src = drafts.find(d => d.id === id);
    if (!src) return null;
    const nextSeatNum = parseInt(src.seat, 10);
    const created = {
      ...src,
      id: newDraftId(),
      native_x: src.native_x + (offset.x || 0),
      native_y: src.native_y + (offset.y || 0),
      seat: Number.isFinite(nextSeatNum) ? String(nextSeatNum + 1) : src.seat,
      error: null,
      _local: true,
    };
    setDrafts(prev => [...prev, created]);
    return created;
  }, [drafts]);

  const _buildBody = (draft) => ({
    chart_id: draft.chart_id || chartId,
    section: String(draft.section),
    row: String(draft.row),
    seat: String(draft.seat),
    template_id: draft.template_id,
    native_center_x: Math.round(draft.native_x * 100) / 100,
    native_center_y: Math.round(draft.native_y * 100) / 100,
    rotation: draft.rotation || 0,
    custom_width: Math.round(draft.width * 100) / 100,
    custom_height: Math.round(draft.height * 100) / 100,
  });

  const _validateMeta = (draft) => {
    if (!draft.section || String(draft.section).trim() === '') return 'Section is required';
    if (draft.row == null || String(draft.row).trim() === '') return 'Row is required';
    if (draft.seat == null || String(draft.seat).trim() === '') return 'Seat is required';
    if (!draft.template_id) return 'No template selected';
    return null;
  };

  const saveDraftRecord = useCallback(async (draft) => {
    if (!token) return { ok: false, error: 'Authentication required' };
    const metaErr = _validateMeta(draft);
    if (metaErr) return { ok: false, error: metaErr };

    try {
      const res = await fetch(`${API_BASE}/api/seat-map/seat-geometries`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(_buildBody(draft)),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) return { ok: true, seat: data.seat };

      let msg = 'Save failed';
      if (data?.detail?.errors && Array.isArray(data.detail.errors)) {
        msg = data.detail.errors.map(e => e.message).join('; ');
      } else if (typeof data?.detail === 'string') {
        msg = data.detail;
      }
      return { ok: false, error: msg };
    } catch (err) {
      return { ok: false, error: err.message || 'Network error' };
    }
  }, [token, chartId]);

  // Save a single draft; removes it on success, marks error on failure
  const saveOne = useCallback(async (id) => {
    const draft = drafts.find(d => d.id === id);
    if (!draft) return { ok: false, error: 'Draft not found' };
    setSaving(true);
    const r = await saveDraftRecord(draft);
    if (r.ok) {
      removeDraft(id);
      if (onSaved) await onSaved();
    } else {
      updateDraft(id, { error: r.error });
    }
    setSaving(false);
    return r;
  }, [drafts, saveDraftRecord, removeDraft, updateDraft, onSaved]);

  // Save every draft; keeps failed ones locally with an error state
  const saveAllDrafts = useCallback(async () => {
    setSaving(true);
    const results = { saved: 0, failed: 0, errors: [] };
    const remaining = [];
    for (const d of drafts) {
      const r = await saveDraftRecord(d);
      if (r.ok) {
        results.saved += 1;
      } else {
        results.failed += 1;
        results.errors.push(`${d.section || '?'} R${d.row || '?'} S${d.seat || '?'}: ${r.error}`);
        remaining.push({ ...d, error: r.error });
      }
    }
    setDrafts(remaining);
    setSaving(false);
    if (results.saved > 0 && onSaved) await onSaved();
    return results;
  }, [drafts, saveDraftRecord, onSaved]);

  return {
    drafts,
    setDrafts,
    addDraft,
    updateDraft,
    removeDraft,
    clearDrafts,
    duplicateDraft,
    saveOne,
    saveAllDrafts,
    saving,
    suggestNextSeat,
    rememberMeta,
    memoryRef,
  };
};

export default useSeatDrafts;
